import React, { useState } from 'react';
import { PeriodoVacaciones, Empleado, TipoLicencia, BalanceEmpleado } from '../tipos/vacaciones';
import FormularioPeriodo from './FormularioPeriodo';
import ModalConfirmacion from './ModalConfirmacion';
import * as periodoService from '../services/periodoService';
import './HistorialLicencias.css';

interface PropiedadesHistorialLicencias {
  periodos: PeriodoVacaciones[];
  empleados: Empleado[];
  tiposLicencia: TipoLicencia[];
  balances: BalanceEmpleado[];
  onDatosActualizados: () => void;
  esAdmin?: boolean;
}

const HistorialLicencias: React.FC<PropiedadesHistorialLicencias> = ({
  periodos,
  empleados,
  tiposLicencia,
  balances,
  onDatosActualizados,
  esAdmin = false,
}) => {
  const [filtroTipo, setFiltroTipo] = useState<string>('all');
  const [filtroAnio, setFiltroAnio] = useState<string>('all');
  const [ordenarPor, setOrdenarPor] = useState<'date' | 'type' | 'days'>('date');
  const [ordenDireccion, setOrdenDireccion] = useState<'asc' | 'desc'>('desc');

  // Estados de modales
  const [mostrarFormPeriodo, setMostrarFormPeriodo] = useState(false);
  const [mostrarConfirmEliminar, setMostrarConfirmEliminar] = useState(false);
  const [periodoSeleccionado, setPeriodoSeleccionado] = useState<PeriodoVacaciones | null>(null);
  const [eliminando, setEliminando] = useState(false);

  const obtenerAniosDisponibles = () => {
    const conjuntoAnios = new Set(periodos.map(p => p.anio));
    const anios = Array.from(conjuntoAnios).sort((a, b) => b - a);
    return anios;
  };

  const periodosFiltrados = periodos
    .filter(periodo => {
      const coincideTipo = filtroTipo === 'all' || periodo.tipo === filtroTipo;
      const coincideAnio = filtroAnio === 'all' || periodo.anio.toString() === filtroAnio;
      return coincideTipo && coincideAnio;
    })
    .sort((a, b) => {
      let comparacion = 0;

      switch (ordenarPor) {
        case 'date':
          comparacion = new Date(a.fechaInicio).getTime() - new Date(b.fechaInicio).getTime();
          break;
        case 'type':
          comparacion = a.tipo.localeCompare(b.tipo);
          break;
        case 'days':
          comparacion = a.dias - b.dias;
          break;
      }

      return ordenDireccion === 'asc' ? comparacion : -comparacion;
    });

  const manejarOrden = (nuevoOrdenarPor: 'date' | 'type' | 'days') => {
    if (ordenarPor === nuevoOrdenarPor) {
      setOrdenDireccion(ordenDireccion === 'asc' ? 'desc' : 'asc');
    } else {
      setOrdenarPor(nuevoOrdenarPor);
      setOrdenDireccion('desc');
    }
  };

  const obtenerIconoTipo = (tipo: string) => {
    switch (tipo) {
      case 'Vacaciones':
        return 'fas fa-umbrella-beach';
      case 'Licencia médica':
        return 'fas fa-heartbeat';
      case 'Día de Beneficio':
        return 'fas fa-star';
      case 'Compensatorio':
        return 'fas fa-clock';
      default:
        return 'fas fa-calendar';
    }
  };

  const obtenerColorTipo = (tipo: string) => {
    switch (tipo) {
      case 'Vacaciones':
        return 'vacaciones';
      case 'Licencia médica':
        return 'medica';
      case 'Día de Beneficio':
        return 'especial';
      case 'Compensatorio':
        return 'compensatorio';
      default:
        return 'vacaciones';
    }
  };

  const formatearFecha = (cadenaFecha: string) => {
    return new Date(cadenaFecha).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatearRangoFechas = (fechaInicio: string, fechaFin: string) => {
    if (fechaInicio === fechaFin) {
      return formatearFecha(fechaInicio);
    }
    return `${formatearFecha(fechaInicio)} - ${formatearFecha(fechaFin)}`;
  };

  const esPasado = (fechaFin: string) => {
    return new Date(fechaFin) < new Date();
  };

  const estadisticasAnio = periodosFiltrados
    .filter(p => filtroAnio === 'all' || p.anio.toString() === filtroAnio)
    .reduce((acumulador, periodo) => {
      acumulador.totalDias += periodo.dias;
      acumulador.totalPeriodos += 1;
      return acumulador;
    }, { totalDias: 0, totalPeriodos: 0 });

  // Handlers de modales
  const abrirNuevaLicencia = () => {
    setPeriodoSeleccionado(null);
    setMostrarFormPeriodo(true);
  };

  const abrirEditarPeriodo = (periodo: PeriodoVacaciones) => {
    setPeriodoSeleccionado(periodo);
    setMostrarFormPeriodo(true);
  };

  const abrirConfirmarEliminar = (periodo: PeriodoVacaciones) => {
    setPeriodoSeleccionado(periodo);
    setMostrarConfirmEliminar(true);
  };

  const manejarGuardarPeriodo = () => {
    setMostrarFormPeriodo(false);
    setPeriodoSeleccionado(null);
    onDatosActualizados();
  };

  const manejarEliminarPeriodo = async () => {
    if (!periodoSeleccionado) return;
    setEliminando(true);
    try {
      await periodoService.remove(periodoSeleccionado.id);
      setMostrarConfirmEliminar(false);
      setPeriodoSeleccionado(null);
      onDatosActualizados();
    } catch (err) {
      console.error('Error al eliminar periodo:', err);
    } finally {
      setEliminando(false);
    }
  };

  return (
    <div className="license-history">
      <div className="history-header">
        <div className="history-header-content">
          <div>
            <h2>Detalle de Licencias y Vacaciones</h2>
            <p>Historial completo de todos los periodos de licencia</p>
          </div>
          {esAdmin && (
            <button className="btn-nuevo" onClick={abrirNuevaLicencia}>
              <i className="fas fa-plus"></i>
              Nueva Licencia
            </button>
          )}
        </div>
      </div>

      <div className="history-filters">
        <div className="filter-group">
          <label htmlFor="type-filter">Tipo:</label>
          <select
            id="type-filter"
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
          >
            <option value="all">Todos los tipos</option>
            <option value="Vacaciones">Vacaciones</option>
            <option value="Licencia médica">Licencia médica</option>
            <option value="Día de Beneficio">Día de Beneficio</option>
            <option value="Compensatorio">Compensatorio</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="year-filter">Anio:</label>
          <select
            id="year-filter"
            value={filtroAnio}
            onChange={(e) => setFiltroAnio(e.target.value)}
          >
            <option value="all">Todos los anios</option>
            {obtenerAniosDisponibles().map(anio => (
              <option key={anio} value={anio}>{anio}</option>
            ))}
          </select>
        </div>

        <div className="results-count">
          <span>{periodosFiltrados.length} periodo(s) encontrado(s)</span>
          {estadisticasAnio.totalDias > 0 && (
            <span className="total-days"> {estadisticasAnio.totalDias} dias totales</span>
          )}
        </div>
      </div>

      <div className="history-table-container">
        <table className="history-table">
          <thead>
            <tr>
              <th>Empleado</th>
              <th
                className={`sortable ${ordenarPor === 'date' ? `sorted-${ordenDireccion}` : ''}`}
                onClick={() => manejarOrden('date')}
              >
                Fechas
                <i className={`fas fa-sort${ordenarPor === 'date' ? `-${ordenDireccion === 'asc' ? 'up' : 'down'}` : ''}`}></i>
              </th>
              <th
                className={`sortable ${ordenarPor === 'days' ? `sorted-${ordenDireccion}` : ''}`}
                onClick={() => manejarOrden('days')}
              >
                Dias
                <i className={`fas fa-sort${ordenarPor === 'days' ? `-${ordenDireccion === 'asc' ? 'up' : 'down'}` : ''}`}></i>
              </th>
              <th
                className={`sortable ${ordenarPor === 'type' ? `sorted-${ordenDireccion}` : ''}`}
                onClick={() => manejarOrden('type')}
              >
                Tipo
                <i className={`fas fa-sort${ordenarPor === 'type' ? `-${ordenDireccion === 'asc' ? 'up' : 'down'}` : ''}`}></i>
              </th>
              <th>Descripcion</th>
              <th>Estado</th>
              {esAdmin && <th>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {periodosFiltrados.map(periodo => {
              const periodoEsPasado = esPasado(periodo.fechaFin);

              return (
                <tr key={periodo.id}>
                  <td data-label="Empleado">
                    <strong>{periodo.nombreEmpleado}</strong>
                  </td>
                  <td data-label="Fechas">
                    <div className="date-range">
                      <strong>
                        {formatearRangoFechas(periodo.fechaInicio, periodo.fechaFin)}
                      </strong>
                    </div>
                  </td>
                  <td data-label="Dias">
                    <span className="days-count">{periodo.dias}</span>
                  </td>
                  <td data-label="Tipo">
                    <div className={`type-badge ${obtenerColorTipo(periodo.tipo)}`}>
                      <i className={obtenerIconoTipo(periodo.tipo)}></i>
                      <span>{periodo.tipo}</span>
                    </div>
                  </td>
                  <td data-label="Descripcion">
                    {periodo.descripcion || '-'}
                  </td>
                  <td data-label="Estado">
                    <span className={`status-badge ${periodoEsPasado ? 'completed' : 'scheduled'}`}>
                      <i className={`fas ${periodoEsPasado ? 'fa-check-circle' : 'fa-clock'}`}></i>
                      {periodoEsPasado ? 'Completado' : 'Programado'}
                    </span>
                  </td>
                  {esAdmin && (
                    <td data-label="Acciones">
                      <div className="acciones-celda">
                        {!periodoEsPasado ? (
                          <>
                            <button
                              className="btn-accion btn-editar"
                              onClick={() => abrirEditarPeriodo(periodo)}
                              title="Editar periodo"
                            >
                              <i className="fas fa-pencil-alt"></i>
                            </button>
                            <button
                              className="btn-accion btn-eliminar-accion"
                              onClick={() => abrirConfirmarEliminar(periodo)}
                              title="Eliminar periodo"
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </>
                        ) : (
                          <span className="sin-acciones">-</span>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {periodosFiltrados.length === 0 && (
        <div className="no-results">
          <i className="fas fa-search"></i>
          <h3>No se encontraron registros</h3>
          <p>No hay periodos de licencia que coincidan con los filtros seleccionados.</p>
        </div>
      )}

      {periodosFiltrados.length > 0 && (
        <div className="history-summary">
          <div className="summary-card">
            <h3>
              Resumen {filtroAnio !== 'all' ? filtroAnio : 'Total'}
              {filtroTipo !== 'all' && ` - ${filtroTipo}`}
            </h3>
            <div className="summary-stats">
              <div className="stat">
                <span className="stat-value">{estadisticasAnio.totalPeriodos}</span>
                <span className="stat-label">Periodos</span>
              </div>
              <div className="stat">
                <span className="stat-value">{estadisticasAnio.totalDias}</span>
                <span className="stat-label">Dias Totales</span>
              </div>
              <div className="stat">
                <span className="stat-value">
                  {estadisticasAnio.totalPeriodos > 0 ? Math.round(estadisticasAnio.totalDias / estadisticasAnio.totalPeriodos * 10) / 10 : 0}
                </span>
                <span className="stat-label">Promedio por Periodo</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modales */}
      {mostrarFormPeriodo && (
        <FormularioPeriodo
          periodo={periodoSeleccionado}
          empleados={empleados}
          tiposLicencia={tiposLicencia}
          balances={balances}
          periodos={periodos}
          onGuardar={manejarGuardarPeriodo}
          onCancelar={() => { setMostrarFormPeriodo(false); setPeriodoSeleccionado(null); }}
        />
      )}

      <ModalConfirmacion
        visible={mostrarConfirmEliminar}
        titulo="Eliminar Periodo"
        mensaje={`Esta seguro de que desea eliminar el periodo de "${periodoSeleccionado?.nombreEmpleado}" (${periodoSeleccionado?.tipo})? Esta accion no se puede deshacer.`}
        onConfirmar={manejarEliminarPeriodo}
        onCancelar={() => { setMostrarConfirmEliminar(false); setPeriodoSeleccionado(null); }}
        cargando={eliminando}
      />
    </div>
  );
};

export default HistorialLicencias;
